import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { HC } from "./theme";
import { Card, SectionHead, Btn, Field, Input, Select } from "./ui";
import { toast } from "sonner";
import { Save, Plus, X, Info } from "lucide-react";

export default function PayrollRules() {
  const [r, setR] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/hr/payroll/rules").then(setR).catch((e) => toast.error(formatApiError(e))); }, []);

  const set = (k, v) => setR((x) => ({ ...x, [k]: v }));
  const setAcc = (k, v) => setR((x) => ({ ...x, accounts: { ...x.accounts, [k]: v } }));
  const setTh = (k, v) => setR((x) => ({ ...x, exception_thresholds: { ...x.exception_thresholds, [k]: v } }));
  const setAbs = (k, v) => setR((x) => ({ ...x, absence_deduction: { ...(x.absence_deduction || {}), [k]: v } }));
  const setAtt = (k, v) => setR((x) => ({ ...x, attendance: { ...(x.attendance || {}), [k]: v } }));

  const save = async () => {
    setBusy(true);
    try { setR(await api.put("/hr/payroll/rules", r)); toast.success("Payroll rules updated"); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  if (!r) return <div className="h-40 rounded-2xl animate-pulse" style={{ background: HC.borderSoft }} />;

  return (
    <div className="space-y-4" data-testid="hr-payroll-rules-page">
      <Card className="!p-4">
        <div className="flex items-start gap-2 text-[12.5px]" style={{ color: HC.muted }}>
          <Info size={15} className="mt-0.5 shrink-0" style={{ color: HC.gold }} />
          Every rate below is configurable — nothing is hardcoded in the application. Changes apply to the next payroll calculation and are written to the audit trail.
        </div>
      </Card>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <Card>
          <SectionHead title="Working time & overtime" />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Working days / month"><Input type="number" data-testid="rule-working-days" value={r.working_days_per_month} onChange={(e) => set("working_days_per_month", Number(e.target.value))} /></Field>
            <Field label="Standard hours / day"><Input type="number" value={r.standard_hours_per_day} onChange={(e) => set("standard_hours_per_day", Number(e.target.value))} /></Field>
            <Field label="Overtime multiplier"><Input type="number" step="0.1" data-testid="rule-ot-multiplier" value={r.overtime_multiplier} onChange={(e) => set("overtime_multiplier", Number(e.target.value))} /></Field>
            <Field label="Overtime enabled">
              <Select value={String(r.overtime_enabled)} onChange={(e) => set("overtime_enabled", e.target.value === "true")}>
                <option value="true">Yes</option><option value="false">No</option>
              </Select>
            </Field>
            <Field label="Deduct unpaid absence">
              <Select data-testid="rule-absence" value={String(r.absence_deduction_enabled)} onChange={(e) => set("absence_deduction_enabled", e.target.value === "true")}>
                <option value="false">No</option><option value="true">Yes</option>
              </Select>
            </Field>
            <Field label="Absence deduction basis">
              <Select data-testid="rule-absence-basis" value={r.absence_deduction?.basis || "daily_basic"}
                onChange={(e) => setAbs("basis", e.target.value)}>
                <option value="daily_basic">Daily basic (basic ÷ working days)</option>
                <option value="pct_of_monthly">% of monthly basic per day</option>
                <option value="fixed_per_day">Fixed ₦ per day</option>
              </Select>
            </Field>
            <Field label={r.absence_deduction?.basis === "fixed_per_day" ? "Amount per absent day (₦)" : "Rate (% of monthly basic per day)"}>
              <Input type="number" step="0.01" data-testid="rule-absence-rate"
                disabled={(r.absence_deduction?.basis || "daily_basic") === "daily_basic"}
                value={r.absence_deduction?.rate ?? 0} onChange={(e) => setAbs("rate", Number(e.target.value))} />
            </Field>
            <Field label="Lateness grace (minutes)">
              <Input type="number" data-testid="rule-late-grace" value={r.attendance?.lateness_grace_minutes ?? 10}
                onChange={(e) => setAtt("lateness_grace_minutes", Number(e.target.value))} />
            </Field>
            <Field label="Minimum shift completion (%)">
              <Input type="number" data-testid="rule-min-completion" value={r.attendance?.min_shift_completion_pct ?? 50}
                onChange={(e) => setAtt("min_shift_completion_pct", Number(e.target.value))} />
            </Field>
            <Field label="NHF (% of basic)"><Input type="number" step="0.1" value={r.nhf_pct} onChange={(e) => set("nhf_pct", Number(e.target.value))} /></Field>
          </div>
        </Card>

        <Card>
          <SectionHead title="Pension" sub="Applied to basic salary" />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Pension enabled">
              <Select value={String(r.pension_enabled)} onChange={(e) => set("pension_enabled", e.target.value === "true")}>
                <option value="true">Yes</option><option value="false">No</option>
              </Select>
            </Field>
            <Field label="Employee %"><Input type="number" step="0.1" data-testid="rule-pension-ee" value={r.pension_employee_pct} onChange={(e) => set("pension_employee_pct", Number(e.target.value))} /></Field>
            <Field label="Employer %"><Input type="number" step="0.1" data-testid="rule-pension-er" value={r.pension_employer_pct} onChange={(e) => set("pension_employer_pct", Number(e.target.value))} /></Field>
          </div>
          <SectionHead title="PAYE relief" />
          <div className="grid grid-cols-2 gap-3">
            <Field label="PAYE enabled">
              <Select data-testid="rule-paye" value={String(r.paye_enabled)} onChange={(e) => set("paye_enabled", e.target.value === "true")}>
                <option value="true">Yes</option><option value="false">No</option>
              </Select>
            </Field>
            <Field label="Consolidated relief (fixed, annual)"><Input type="number" value={r.cra_fixed} onChange={(e) => set("cra_fixed", Number(e.target.value))} /></Field>
            <Field label="Consolidated relief (% of gross)"><Input type="number" step="0.1" value={r.cra_pct_of_gross} onChange={(e) => set("cra_pct_of_gross", Number(e.target.value))} /></Field>
          </div>
        </Card>

        <Card>
          <SectionHead title="PAYE bands (annual taxable income)" sub="Progressive — leave the last band's cap empty for the top rate" />
          {(r.paye_bands || []).map((b, i) => (
            <div key={i} className="flex items-center gap-2 mb-2">
              <Input type="number" placeholder="Up to (blank = remainder)" value={b.up_to ?? ""} data-testid={`band-cap-${i}`}
                onChange={(e) => { const a = [...r.paye_bands]; a[i] = { ...b, up_to: e.target.value === "" ? null : Number(e.target.value) }; set("paye_bands", a); }} />
              <Input type="number" step="0.1" className="w-28" value={b.rate} data-testid={`band-rate-${i}`}
                onChange={(e) => { const a = [...r.paye_bands]; a[i] = { ...b, rate: Number(e.target.value) }; set("paye_bands", a); }} />
              <span className="text-[12px]" style={{ color: HC.soft }}>%</span>
              <button onClick={() => set("paye_bands", r.paye_bands.filter((_, j) => j !== i))} style={{ color: HC.red }}><X size={16} /></button>
            </div>
          ))}
          <Btn variant="outline" className="h-9" data-testid="add-band"
            onClick={() => set("paye_bands", [...(r.paye_bands || []), { up_to: null, rate: 0 }])}><Plus size={14} /> Add band</Btn>
        </Card>

        <Card>
          <SectionHead title="Other deductions" sub="Fixed amount or % of gross, applied to every employee" />
          {(r.other_deductions || []).map((d, i) => (
            <div key={i} className="flex items-center gap-2 mb-2">
              <Input placeholder="Name" value={d.name || ""} onChange={(e) => { const a = [...r.other_deductions]; a[i] = { ...d, name: e.target.value }; set("other_deductions", a); }} />
              <Select className="w-32" value={d.type || "fixed"} onChange={(e) => { const a = [...r.other_deductions]; a[i] = { ...d, type: e.target.value }; set("other_deductions", a); }}>
                <option value="fixed">Fixed</option><option value="percent">% of gross</option>
              </Select>
              <Input type="number" className="w-32" value={d.value || 0} onChange={(e) => { const a = [...r.other_deductions]; a[i] = { ...d, value: Number(e.target.value) }; set("other_deductions", a); }} />
              <button onClick={() => set("other_deductions", r.other_deductions.filter((_, j) => j !== i))} style={{ color: HC.red }}><X size={16} /></button>
            </div>
          ))}
          <Btn variant="outline" className="h-9" data-testid="add-deduction"
            onClick={() => set("other_deductions", [...(r.other_deductions || []), { name: "", type: "fixed", value: 0 }])}><Plus size={14} /> Add deduction</Btn>
        </Card>

        <Card>
          <SectionHead title="Accounting mapping (Layer 7)" sub="Journal accounts used when payroll is posted" />
          <div className="grid grid-cols-2 gap-3">
            {[["salary_expense", "Salary expense"], ["employer_pension_expense", "Employer pension expense"],
              ["paye_liability", "PAYE liability"], ["pension_liability", "Pension liability"],
              ["other_deduction_liability", "Other deductions liability"], ["net_payroll_liability", "Net salaries payable"]].map(([k, l]) => (
              <Field key={k} label={l}><Input data-testid={`acc-${k}`} value={r.accounts?.[k] || ""} onChange={(e) => setAcc(k, e.target.value)} /></Field>
            ))}
          </div>
        </Card>

        <Card>
          <SectionHead title="Exception thresholds" sub="Drives the pre-approval payroll validation" />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Max overtime hours / employee"><Input type="number" value={r.exception_thresholds?.overtime_hours_max} onChange={(e) => setTh("overtime_hours_max", Number(e.target.value))} /></Field>
            <Field label="Payroll variance alert (%)"><Input type="number" value={r.exception_thresholds?.payroll_variance_pct} onChange={(e) => setTh("payroll_variance_pct", Number(e.target.value))} /></Field>
            <Field label="Minimum net pay"><Input type="number" value={r.exception_thresholds?.min_net_pay} onChange={(e) => setTh("min_net_pay", Number(e.target.value))} /></Field>
          </div>
        </Card>
      </div>

      <div className="flex justify-end">
        <Btn onClick={save} disabled={busy} data-testid="rules-save"><Save size={15} /> Save payroll rules</Btn>
      </div>
    </div>
  );
}
